/**
 * Which agents the New session dialog offers on a machine, and why one cannot start there.
 *
 * Claude Code and Codex are always offered (a missing CLI is said inline, with how to install
 * it). An ACP agent (Gemini CLI, Cursor, Copilot CLI, ..., ADR 0049) is offered only on a
 * machine whose agent lists the kind in agentKinds and found its CLI installed (runtimes), in
 * the order ACP_AGENTS gives.
 */
import { ACP_AGENTS, DEFAULT_AGENT_KIND, KNOWN_AGENT_KINDS, acpAgent, agentKindLabel, machineStartsKind, runtimeOf } from '@orgus/protocol';
import type { AgentKind, Machine } from '@orgus/protocol';

type MachineAgents = Pick<Machine, 'agentKinds' | 'runtimes'>;

/** Up to this many agents the picker is a segmented control; above it, a select. */
export const SEGMENTED_MAX_KINDS = 3;

/** The agent kinds the dialog offers on a machine. */
export function startableKinds(m: MachineAgents | null | undefined): AgentKind[] {
  const out: AgentKind[] = [...KNOWN_AGENT_KINDS];
  if (!m) return out;
  for (const d of ACP_AGENTS) {
    if (machineStartsKind(m, d.kind) && runtimeOf(m, d.kind)?.installed) out.push(d.kind);
  }
  return out;
}

/** A kind to keep (remembered, or chosen before the machine changed): itself where the machine offers it, else Claude Code. */
export function keepKind(kind: unknown, m: MachineAgents | null | undefined): AgentKind {
  return typeof kind === 'string' && startableKinds(m).includes(kind) ? kind : DEFAULT_AGENT_KIND;
}

/** How to install a kind's CLI, as a clause: "Install it there (npm install -g @openai/codex)". */
function installHint(kind: AgentKind): string {
  if (kind === 'codex') return 'Install it there (npm install -g @openai/codex)';
  const bridge = acpAgent(kind)?.bridge;
  return bridge ? `Install it there (npm install -g ${bridge})` : 'Install it there';
}

/**
 * Why this machine cannot run the agent, or null. An agent that does not list the kind
 * (one that predates adapters) would start Claude Code instead, so it is refused. A new
 * agent that has not finished looking for CLIs yet is trusted.
 */
export function agentMachineError(kind: AgentKind, m: Pick<Machine, 'name' | 'runtimes' | 'agentKinds'> | undefined): string | null {
  if (!m) return null;
  if (!machineStartsKind(m, kind)) return `Update the Orgus agent on ${m.name} to start ${agentKindLabel(kind)} there. The agent it runs now can only start Claude Code.`;
  if (!m.runtimes) return null;
  const r = runtimeOf(m, kind);
  if (r?.installed) return null;
  return `${agentKindLabel(kind)} is not installed on ${m.name}. ${installHint(kind)}, or choose another machine.`;
}

/**
 * Something to know before starting that does not stop the start, or null: an ACP agent that
 * answered its last start on this machine by asking for a sign-in.
 */
export function agentMachineNote(kind: AgentKind, m: Pick<Machine, 'name' | 'runtimes'> | undefined): string | null {
  const def = acpAgent(kind);
  if (!def || !m) return null;
  if (runtimeOf(m, kind)?.acp?.signedIn !== false) return null;
  const program = def.launches[0]?.argv[0] ?? kind;
  return `${def.label} was not signed in at its last start on ${m.name}. Run ${program} in a terminal there and sign in, or the start is refused.`;
}
